import { Modal as BaseModal } from '@flyer/components';
import Image from 'next/image';
import { twMerge } from 'tailwind-merge';
import dividerSrc from './divider.png';
import closeSrc from './close.png';
import closeBaseSrc from './close_base.png';
import MotionImage from '@/components/common/MotionImage';
import { IeltsTheme } from '@/types';
import { useAtom } from 'jotai';
import { ieltsThemeAtom } from '@/store/home';

export interface ModalProps {
  open: boolean;
  onClose?: (o: boolean) => void;
  title?: string | JSX.Element;
  children: React.ReactNode;
  wrapClassName?: string;
  titleClassName?: string;
  className?: string;
  disableBackdrop?: boolean;
  centered?: boolean;
  closeIcon?: boolean;
  divider?: boolean;
}

export default function ModalBase(props: ModalProps) {
  const {
    wrapClassName,
    open,
    titleClassName,
    className,
    title,
    onClose,
    children,
    disableBackdrop,
    centered = true,
    closeIcon,
    divider = true,
  } = props;
  const [ieltsTheme] = useAtom(ieltsThemeAtom);

  return (
    <BaseModal
      open={open}
      centered={centered}
      lockScroll={false}
      onClose={onClose}
      className={twMerge(
        'max-h-full outline-none',
        'relative rounded-[8px] border-[3px] border-[#052B62]',
        '[background:radial-gradient(41.41%_41.41%_at_50.04%_8.59%,_#145199_0%,_#053478_100%)]',
        '[box-shadow:0px_0px_25px_0px_#fff]',
        'text-white text-center',
        'min-w-[650px] max-lg:min-w-[90%] max-md:max-w-[90%]',
        wrapClassName
      )}
      disableBackdrop={disableBackdrop}
    >
      {closeIcon && (
        <>
          {ieltsTheme === IeltsTheme.GAMIFICATION ? (
            <MotionImage
              src={closeSrc}
              alt="close"
              className="w-[56px] h-[56px] max-md:w-10 max-md:h-10 absolute -top-[20px] -right-[20px] z-10 cursor-pointer"
              onClick={() => onClose?.(false)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            />
          ) : (
            <MotionImage
              src={closeBaseSrc}
              alt="close"
              className="w-[30px] h-[30px] max-md:w-10 max-md:h-10 absolute top-[10px] right-[5px] z-10 cursor-pointer bg-white p-[4px]"
              onClick={() => onClose?.(false)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            />
          )}
        </>
      )}

      {title && (
        <>
          <h1
            className={twMerge(
              'm-0 [display:-webkit-inline-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical] overflow-hidden text-ellipsis pt-[4%] pb-[3%] max-lg:text-2xl max-md:text-[22px]',
              titleClassName
            )}
          >
            {title}
          </h1>
          {divider && <Image alt="divider" src={dividerSrc} className="w-full h-[85px] object-fill object-center -mb-[50px]" />}
        </>
      )}

      <div className={twMerge('px-[clamp(20px,10%,72px)] pb-[clamp(10px,10%,42px)] text-white', !title && 'pt-[clamp(10px,10%,62px)]', className)}>
        {children}
      </div>
    </BaseModal>
  );
}
